import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import ErrorFallback from './ErrorFallback.component'
import type {
  ErrorBoundaryProps,
  ErrorBoundaryState,
} from './ErrorBoundary.types'
import { logger } from '@utils/safeLogger'

function haveResetKeysChanged(
  prevKeys: ReadonlyArray<string | number> | undefined,
  nextKeys: ReadonlyArray<string | number> | undefined,
): boolean {
  if (prevKeys === nextKeys) {
    return false
  }
  if (prevKeys == null || nextKeys == null) {
    return prevKeys !== nextKeys
  }
  if (prevKeys.length !== nextKeys.length) {
    return true
  }
  return prevKeys.some((key, index) => key !== nextKeys[index])
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
  }

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('ErrorBoundary caught a render error', {
      error,
      componentStack: errorInfo.componentStack,
    })
  }

  public componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { hasError } = this.state
    const { resetKeys } = this.props

    if (hasError && haveResetKeysChanged(prevProps.resetKeys, resetKeys)) {
      this.reset()
    }
  }

  private reset = (): void => {
    this.setState({ hasError: false })
  }

  public render(): ReactNode {
    const { hasError } = this.state
    const { children, variant = 'page' } = this.props

    if (hasError) {
      return <ErrorFallback variant={variant} onReset={this.reset} />
    }

    return children
  }
}

export default ErrorBoundary
